import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ConsentBanner from "@/components/site/ConsentBanner";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import { LangProvider, useLang } from "@/i18n/LanguageContext";
import { restoreAnalyticsConsent, trackPageView, trackEvent } from "@/lib/analytics";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import Analyses from "@/pages/Analyses";
import ArticleDetail from "@/pages/ArticleDetail";
import Solutions from "@/pages/Solutions";
import SolutionDetail from "@/pages/SolutionDetail";
import Expertises from "@/pages/Expertises";
import ExpertiseDetail from "@/pages/ExpertiseDetail";
import APropos from "@/pages/APropos";
import LegalPage from "@/pages/LegalPage";
import Sourcing from "@/pages/Sourcing";
import Programmes from "@/pages/Programmes";
import Admin from "@/pages/Admin";
import { Toaster } from "@/components/ui/sonner";

// Anciennes URLs WordPress → nouvelles pages (plan de migration SEO, cf. /app/memory/migration-seo.md).
// Les vraies 301 HTTP seront posées au niveau de l'hébergement lors de la bascule DNS ;
// en attendant, ce catch-all redirige proprement les visiteurs et les liens indexés.
const LEGACY_REDIRECTS = {
    "/accueil": "/",
    "/contacts": "/contact",
    "/services": "/solutions",
    "/nos-offres": "/solutions",
    "/export360": "/solutions/export360",
    "/export360-2": "/solutions/export360",
    "/accompagnement-salons-internationaux": "/expertises/salons-internationaux",
    "/missions-b2b-internationales": "/expertises/missions-b2b",
    "/closing-commercial-export": "/expertises/prospection-b2b-closing",
    "/closing-commercial-export-2": "/expertises/prospection-b2b-closing",
    "/veilles-et-etudes-de-marche": "/expertises/etudes-de-marche",
    "/faq": "/solutions",
    "/team": "/a-propos",
    "/careers": "/contact",
    "/offres-demploi": "/contact",
    "/reservation": "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr",
    "/blog-grid": "/analyses",
    "/case-studies": "/a-propos",
    "/politique-de-confidentialite-2": "/politique-de-confidentialite",
    "/category/actualites-sur-les-marches-internationaux": "/analyses",
    "/category/etudes-de-cas-de-succes": "/analyses",
    "/category/guides-pratiques-pour-lexportation": "/analyses",
    "/category/non-classe": "/analyses",
    "/category/tendances-du-commerce-international": "/analyses",
    "/a-mon-avis-il-ny-a-quavec-un-dialogue-constructif-on-peut-assurer-un-avenir-durable-et-equitable-pour-nos-tous": "/analyses/a-mon-avis-il-ny-a-quavec-un-dialogue-constructif-on-peut-assurer-un-avenir-durable-et-equitable-pour-nos-tous",
    "/classement-mondial-bonheur-2023": "/analyses/classement-mondial-bonheur-2023",
    "/debat-defis-commerce-mondial": "/analyses/debat-defis-commerce-mondial",
    "/l-essor-inevitable-de-l-intelligence-artificielle-opportunites-et-defis": "/analyses/l-essor-inevitable-de-l-intelligence-artificielle-opportunites-et-defis",
    "/mission-maroc-accomplie-weyb": "/analyses/mission-maroc-accomplie-weyb",
    "/le-commerce-international-un-pilier-pour-les-objectifs-de-developpement-durable": "/analyses/le-commerce-international-un-pilier-pour-les-objectifs-de-developpement-durable",
    "/rapport-sur-les-risques-globaux-2024-eclairages-essentiels": "/analyses/rapport-sur-les-risques-globaux-2024-eclairages-essentiels",
    "/nouveau-modele-economique-travail-centre-humain": "/analyses/nouveau-modele-economique-travail-centre-humain",
    "/nouvelle-ere-indicateurs-pib-bib": "/analyses/nouvelle-ere-indicateurs-pib-bib",
    "/secrets-croissance-economique-au-dela-pib": "/analyses/secrets-croissance-economique-au-dela-pib",
    "/situation-economique-argentine-dollarisation": "/analyses/situation-economique-argentine-dollarisation",
    "/slim-nouvelle-ere-exploration-lunaire": "/analyses/slim-nouvelle-ere-exploration-lunaire",
    "/weyb-devient-activateur-france-num-reference": "/analyses/weyb-devient-activateur-france-num-reference",
};

function useLenis() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function AnalyticsRouteTracker() {
    const location = useLocation();
    useEffect(() => { restoreAnalyticsConsent(); }, []);
    useEffect(() => { trackPageView(); }, [location.pathname, location.search]);
    useEffect(() => {
        // Événements GA4 délégués : clics Cal.com / Export360 / WhatsApp / email / téléphone
        const onClick = (e) => {
            const a = e.target.closest?.("a[href]");
            if (!a) return;
            const href = a.getAttribute("href") || "";
            const page = location.pathname;
            if (href.includes("cal.com/")) trackEvent("cta_calcom_click", { page });
            else if (href.includes("export360.fr")) trackEvent("export360_click", { page });
            else if (href.includes("wa.me/")) trackEvent("whatsapp_click", { page });
            else if (href.startsWith("mailto:")) trackEvent("email_click", { page });
            else if (href.startsWith("tel:")) trackEvent("phone_click", { page });
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, [location.pathname]);
    return null;
}

// Vraie page 404 (design WEYB) — affichée pour toute URL inconnue et non couverte par les redirections legacy.
function LegacyOrNotFound() {
    const location = useLocation();
    const { t, prefix } = useLang();
    const [missing, setMissing] = useState(false);
    useEffect(() => {
        const bare = location.pathname.replace(/^\/en(?=\/|$)/, "").replace(/\/+$/, "") || "/";
        const target = LEGACY_REDIRECTS[bare];
        if (target) {
            window.location.replace(target);
        } else {
            // Vraie page 404 : titre dédié + noindex (le statut HTTP 404 n'est pas émettable côté plateforme)
            document.title = `${t("notfound.title")} — WEYB`;
            let m = document.head.querySelector('meta[name="robots"]');
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute("name", "robots");
                document.head.appendChild(m);
            }
            m.setAttribute("content", "noindex, nofollow");
            setMissing(true);
        }
    }, [location.pathname]);
    if (!missing) return null;
    return (
        <section data-testid="page-404" className="bg-weyb-ink text-white min-h-[80vh] flex items-center pt-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-weyb-yellow mb-6">{t("notfound.eyebrow")}</p>
                <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-tight max-w-3xl">{t("notfound.title")}</h1>
                <p className="mt-6 text-white/55 text-base md:text-lg max-w-xl leading-relaxed">{t("notfound.text")}</p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <Link to={prefix || "/"} data-testid="notfound-home" className="inline-flex items-center gap-2 bg-white text-weyb-ink hover:bg-weyb-yellow px-6 py-3 rounded-sm text-sm font-semibold transition-colors">
                        {t("notfound.home")}
                    </Link>
                    <Link to={`${prefix}/solutions`} data-testid="notfound-solutions" className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-6 py-3 rounded-sm text-sm font-medium transition-colors">
                        {t("notfound.solutions")}
                    </Link>
                    <Link to={`${prefix}/contact`} data-testid="notfound-contact" className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-6 py-3 rounded-sm text-sm font-medium transition-colors">
                        {t("notfound.contact")}
                    </Link>
                </div>
            </div>
        </section>
    );
}

function Layout() {
    return (
        <LangProvider>
            <Header />
            <main>
                <Outlet />
            </main>
            <Footer />
            <ConsentBanner />
            <WhatsAppButton />
            <Toaster position="top-center" richColors />
        </LangProvider>
    );
}

const PAGE_ROUTES = (
    <>
        <Route index element={<Home />} />
        <Route path="contact" element={<Contact />} />
        <Route path="analyses" element={<Analyses />} />
        <Route path="analyses/:slug" element={<ArticleDetail />} />
        <Route path="solutions" element={<Solutions />} />
        <Route path="solutions/programmes-appui-formation" element={<Programmes />} />
        <Route path="solutions/:slug" element={<SolutionDetail />} />
        <Route path="expertises" element={<Expertises />} />
        <Route path="expertises/:slug" element={<ExpertiseDetail />} />
        <Route path="a-propos" element={<APropos />} />
        <Route path="sourcing" element={<Sourcing />} />
        <Route path="mentions-legales" element={<LegalPage kind="mentions" />} />
        <Route path="politique-de-confidentialite" element={<LegalPage kind="confidentialite" />} />
        <Route path="*" element={<LegacyOrNotFound />} />
    </>
);

function App() {
    useLenis();
    return (
        <div className="App">
            <BrowserRouter>
                <ScrollToTop />
                <AnalyticsRouteTracker />
                <Routes>
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/en" element={<Layout />}>
                        {PAGE_ROUTES}
                    </Route>
                    <Route path="/" element={<Layout />}>
                        {PAGE_ROUTES}
                    </Route>
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
